import { lazy, Suspense, useEffect } from 'react';
import { Outlet, useLocation, useRouteLoaderData } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { useSWEffect } from '@remix-pwa/sw';
import { ClientOnly } from 'remix-utils/client-only';

import { RECOMMENDATIONS_EMBED_PATH } from '~/constants/recommendationsEmbed';
import { CsrfProvider } from '~/context/CsrfProvider';
import { NectarAnalyticsProvider } from '~/context/NectarAnalyticsProvider';
import { PageProvider } from '~/context/PageProvider';
import { QueryClientProvider } from '~/context/QueryClientProvider';
import { isNectarConsoleEnabled } from '~/devtools/isNectarConsoleEnabled';
import useCreateVisitToken from '~/hooks/useLoaders/useCreateVisitToken';
import type { NectarAnalyticsSettings } from '~/hooks/useNectarAnalytics';
import useNotifications from '~/hooks/useNotifications';
import { usePrivatePublicationRedirect } from '~/hooks/usePrivatePublicationRedirect';
import { usePushNotification } from '~/hooks/usePushNotification';
import useRemoveDraftPreviewCookie from '~/hooks/useRemoveDraftPreviewCookie';
import useRemoveParams from '~/hooks/useRemoveParams';
import { useScrollLockCleanup } from '~/hooks/useScrollLockCleanup';
import useSetLastViewedResource from '~/hooks/useSetLastViewedResource';
import useJSDisabledSubmissionToasts from '~/hooks/useSubmissionErrorToast';
import useCookies, { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';
import usePWAReloadCheck from '~/hooks/useUtils/usePWAReloadCheck';
import useSiteLanguage from '~/hooks/useUtils/useSiteLanguage';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import useUserStatus from '~/hooks/useUtils/useUserStatus';
import { NotificationProvider } from '~/notifications';
import type { RootLoaderData } from '~/root';
import type { CommunityPostLoaderData } from '~/routes/community/posts/$id/index';
import type { PostLoaderData } from '~/routes/p/$slug';
import { useAdvisoryErrorCleanup } from '~/services/sentry/sentryAdvisory';
import { deriveCommunityAnalyticsContext } from '~/utils/communityAnalyticsContext';
import { isCrawlerClient } from '~/utils/isCrawler';

import AdminBadge from '../AdminBadge';
import AnalyticsTracker from '../AnalyticsTracker';
import { getAppGlobals } from '../AppGlobals/utils';
import { BirdieLoader } from '../BirdieLoader';
import BeehiivHoney from '../CookieBanner';
import { DevToolsPanel } from '../DevToolsPanel/DevToolsPanel';
import { isCommunityInstallPath } from '../PWA/pwaPromptConfig';
import HumanEmbed from '../Scripts/HumanEmbed';
import StytchEmbed from '../Scripts/StytchEmbed';

const appGlobals = getAppGlobals();

// Lazily loaded so the dev console UI is split into its own chunk and only
// fetched in environments where it's enabled (see isNectarConsoleEnabled).
const NectarConsole = lazy(() => import('../NectarConsole').then((m) => ({ default: m.NectarConsole })));

const App = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isCommunityRoute = isCommunityInstallPath(location.pathname);
  const isCrawlerVisit = isCrawlerClient();
  const isRecommendationsEmbedRoute = location.pathname === RECOMMENDATIONS_EMBED_PATH;

  const {
    site,
    isLoggedIn,
    subscription,
    admin,
    showLoggedInToast,
    isStagingPreview,
    isMasquerading,
    masqueradeEmail,
    SENTRY_RELEASE,
    isFetchingSubscription,
  } = useTypedLoaderData<RootLoaderData>();

  if (!site) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  const publication = site.publication;

  useSWEffect();

  // [ADVISORY ERROR RELOAD FLAG CLEANUP] Clear the flag on successful page load
  useAdvisoryErrorCleanup();

  // [SCROLL LOCK CLEANUP] Clear leftover Headless UI scroll lock styles after navigation
  useScrollLockCleanup();

  // [PRIVATE PUBLICATION] Redirect to home if private and not logged in
  // This handles SPA navigation since clientLoader only runs on initial page load
  usePrivatePublicationRedirect({
    isPrivate: publication?.private ?? false,
    requireSubscriberApproval: publication?.require_subscriber_approval ?? false,
    isLoggedIn,
    isStagingPreview,
    isFetchingSubscription,
  });

  // [USER STATUS] Sets the user status based on the subscription tier and whether or not they are logged in
  const { isLoggedInOrSubscribed, isPremiumSubscription } = useUserStatus({
    isLoggedIn,
    subscriptionTier: subscription?.tier,
    subscriptionStatus: subscription?.status,
  });

  // [COOKIES] Sets the user-specific cookies
  // Note: Only set cookies client side when users are not in a session
  // The Set-cookie header server side will impact our ability to cache web pages for performance
  // *cookieSet* is a way of letting us know that app has mounted and we have retrieved all available cookies
  const { cookies, setCookie, cookiesSet, removeCookie } = useCookies({
    initialCookies: {
      popup_closed: isLoggedInOrSubscribed,
      email_capture_disabled: isLoggedInOrSubscribed,
      [ACTIVE_UPGRADE_COOKIE_NAME]: isPremiumSubscription,
    },
  });
  useRemoveDraftPreviewCookie();

  // [LANGUAGE] Sets the language for the site based on the publication language
  useSiteLanguage({ language: publication?.language, setCookie });

  // [VISIT TOKEN] Creates a visit token for the user
  // We use visit tokens to track user site visits + page views and more
  // It is critical to many parts of our web analytics that this is set before signups and page views
  const visitTokenCookie = Boolean(cookies.visit_token);
  useCreateVisitToken({
    shouldCreateVisitToken: cookiesSet && !visitTokenCookie && !isCrawlerVisit,
    setCookie,
  });

  useSetLastViewedResource({ setCookie, cookiesSet });

  // [REMOVE PARAMS] Removes query params from the url
  // We occasionally user query params to pass state between pages
  // To prevent params from impacting state more than once, clear them from the url
  useRemoveParams(['status', 'upgraded']);

  // [NOTIFICATIONS] Handles the notifications for the site
  useJSDisabledSubmissionToasts();
  useNotifications({ showLoggedInToast: isLoggedIn && showLoggedInToast });

  const publicationPwaEnabled = Boolean(publication?.web_template?.pwa_enabled);
  usePushNotification({
    icon: publicationPwaEnabled && !isCommunityRoute ? publication?.logo?.thumb?.url : undefined,
  });

  usePWAReloadCheck({ sentryRelease: SENTRY_RELEASE });

  // [NECTAR ANALYTICS] Get post ID if on a post page
  const postRouteData = useRouteLoaderData('routes/p/$slug') as PostLoaderData | undefined;
  const currentPostId = postRouteData?.post?.id;

  // [NECTAR ANALYTICS] Derive community context on community routes (mirrors the postId pattern)
  const communityLayoutData = useRouteLoaderData('routes/community') as
    | { community?: { id: string } | null; spaces?: Array<{ id: string; slug: string }> }
    | undefined;
  const communityPostData = useRouteLoaderData('routes/community/posts/$id/index') as
    | CommunityPostLoaderData
    | undefined;
  const communityContext = deriveCommunityAnalyticsContext({
    pathname: location.pathname,
    community: communityLayoutData?.community,
    spaces: communityLayoutData?.spaces,
    post: communityPostData?.communityPost,
  });

  // Dispatch custom event after React hydration completes
  // This ensures embed support scripts wait for hydration before processing messages
  useEffect(() => {
    document.dispatchEvent(new CustomEvent('beehiiv:hydration_completed'));
  }, []);

  useEffect(() => {
    if (!isFetchingSubscription && (searchParams.get('jwt_token') || searchParams.get('admin_token'))) {
      searchParams.delete('jwt_token');
      searchParams.delete('admin_token');
      setSearchParams(searchParams, { preventScrollReset: true });
    }
  }, [searchParams, isFetchingSubscription, setSearchParams]);

  // Analytics configuration
  const analyticsSettings: NectarAnalyticsSettings = {
    batchSize: publication.nectar_analytics_settings.batch_size,
    batchTimeoutMs: publication.nectar_analytics_settings.batch_timeout_ms,
    pageViewTracking: publication.nectar_analytics_settings.page_view_tracking,
    clickTracking: publication.nectar_analytics_settings.click_tracking,
    ipTracking: publication.nectar_analytics_settings.ip_tracking,
  };

  const analyticsProps = {
    enabled: publication.nectar_analytics_enabled && !isCrawlerVisit,
    nectarId: publication.nectar_id,
    publicationId: publication.id,
    siteId: site.id,
    postId: currentPostId,
    communityId: communityContext.communityId,
    communitySpaceId: communityContext.communitySpaceId,
    subscriptionId: subscription?.id,
    email: subscription?.email,
    settings: analyticsSettings,
  };

  return (
    <CsrfProvider>
      <NotificationProvider>
        <QueryClientProvider>
          <HumanEmbed />
          <StytchEmbed />
          <NectarAnalyticsProvider analyticsProps={analyticsProps}>
            <PageProvider
              cookies={cookies}
              setCookie={setCookie}
              cookiesSet={cookiesSet}
              removeCookie={removeCookie}
              recaptchaSiteKey={appGlobals.CLOUDFLARE_TURNSTILE_SITE_KEY}
              visitToken={cookies.visit_token}
              referralId={cookies.referral_id}
              currentPath={location.pathname}
              subscription={subscription}
              isStagingPreview={isStagingPreview}
              isMasquerading={isMasquerading}
              masqueradeEmail={masqueradeEmail}
            >
              {!isRecommendationsEmbedRoute && (
                <ClientOnly>{() => <BirdieLoader publication={publication} isAdmin={Boolean(admin)} />}</ClientOnly>
              )}
              {import.meta.env.DEV ? <ClientOnly>{() => <DevToolsPanel />}</ClientOnly> : null}
              {isNectarConsoleEnabled() ? (
                <ClientOnly>
                  {() => (
                    <Suspense fallback={null}>
                      <NectarConsole />
                    </Suspense>
                  )}
                </ClientOnly>
              ) : null}
              {admin && !isRecommendationsEmbedRoute && (
                <AdminBadge
                  setCookie={setCookie}
                  justLoggedIn={cookiesSet && !cookies.admin_logged_in && Boolean(admin)}
                />
              )}
              {!isRecommendationsEmbedRoute && (
                <>
                  <AnalyticsTracker />
                  <BeehiivHoney
                    isCookieBannerEnabled={site?.viewable_site_version?.settings?.cookie_banner_enabled}
                    cookies={cookies}
                    setCookie={setCookie}
                    ga4ConsentModeEnabled={Boolean(
                      publication?.ga4_consent_mode_enabled &&
                        (site?.viewable_site_version?.settings?.ga4_pixel_id || publication.google_analytics_4_pixel),
                    )}
                    privacyPolicyUrl={site?.viewable_site_version?.settings?.privacy_policy_url}
                    termsOfUseUrl={site?.viewable_site_version?.settings?.terms_of_use_url}
                  />
                </>
              )}
              <Outlet
                context={{
                  publicationId: publication?.id,
                  publicationBeehiivBranding: publication?.beehiiv_branding,
                  publicationName: publication?.name,
                  publicationLogoUrl: publication?.logo?.url,
                  publicationUtmSource: publication?.footer_referral_utm_source,
                  publicationCopyrightText: publication?.copyright_text,
                  recaptchaSiteKey: appGlobals.CLOUDFLARE_TURNSTILE_SITE_KEY,
                  cookies,
                  setCookie,
                  cookiesSet,
                  removeCookie,
                  visitToken: cookies.visit_token,
                  referralId: cookies.referral_id,
                  admin,
                  isAdmin: Boolean(admin),
                  isLoggedIn,
                }}
              />
              {/* PWA is disabled on orchid-v2 sites */}
              {/* {publicationPwaEnabled && !isCommunityRoute && (
            <>
              <InstallPrompt />
              <EnablePushNotificationPrompt />
            </>
          )} */}
            </PageProvider>
          </NectarAnalyticsProvider>
        </QueryClientProvider>
      </NotificationProvider>
    </CsrfProvider>
  );
};

export default App;
